import type { GatewayProvider } from '../../../types/payment';

export const CHARGE_TYPE_LABELS: Record<string, string> = {
  RECURRING: 'Recorrente',
  SINGLE: 'Pagamento único',
};

export const BILLING_TYPE_LABELS: Record<string, string> = {
  CREDIT_CARD: 'Crédito',
  PIX: 'Pix',
  BOLETO: 'Boleto',
  UNDEFINED: 'Escolha no link',
};

export const SUBSCRIPTION_PAYMENT_METHOD_LABELS: Record<'pix' | 'credit_card', string> = {
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
};

export const ASAAS_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS: Array<{
  value: 'pix' | 'credit_card';
  label: string;
}> = [
  { value: 'pix', label: SUBSCRIPTION_PAYMENT_METHOD_LABELS.pix },
  { value: 'credit_card', label: SUBSCRIPTION_PAYMENT_METHOD_LABELS.credit_card },
];

export const STRIPE_SUBSCRIPTION_PAYMENT_METHOD_OPTIONS: Array<{
  value: 'credit_card';
  label: string;
}> = [
  { value: 'credit_card', label: SUBSCRIPTION_PAYMENT_METHOD_LABELS.credit_card },
];

export const CYCLE_LABELS: Record<string, string> = {
  monthly: 'Mensal',
  MONTHLY: 'Mensal',
  bimonthly: 'Bimestral',
  BIMONTHLY: 'Bimestral',
  weekly: 'Semanal',
  WEEKLY: 'Semanal',
  biweekly: 'Quinzenal',
  BIWEEKLY: 'Quinzenal',
  quarterly: 'Trimestral',
  QUARTERLY: 'Trimestral',
  semestral: 'Semestral',
  semiannually: 'Semestral',
  SEMIANNUALLY: 'Semestral',
  yearly: 'Anual',
  YEARLY: 'Anual',
};

export const STATUS_LABELS: Record<string, string> = {
  PAID: 'Pago',
  PENDING: 'Pendente',
  CANCELED: 'Cancelado',
  REMOVED: 'Cancelado',
  EXPIRED: 'Vencido',
  FAILED: 'Falha',
  REFUNDED: 'Reembolsado',
  PAUSED: 'Pausado',
  CREDIT: 'Crédito',
  UNKNOWN: 'Desconhecido',
};

export const STATUS_ICON: Record<string, { name: string; color: string }> = {
  PAID: { name: 'check_circle', color: 'var(--color-solid-success)' },
  PENDING: { name: 'pending', color: 'var(--color-solid-warning)' },
  CANCELED: { name: 'cancel', color: 'var(--color-solid-danger)' },
  REMOVED: { name: 'cancel', color: 'var(--color-solid-danger)' },
  EXPIRED: { name: 'event_busy', color: 'var(--color-solid-danger)' },
  FAILED: { name: 'error', color: 'var(--color-solid-danger)' },
  REFUNDED: { name: 'undo', color: 'var(--color-solid-info)' },
  PAUSED: { name: 'pause_circle', color: 'var(--color-brand-secondary)' },
  CREDIT: { name: 'payments', color: 'var(--color-brand-primary)' },
  UNKNOWN: { name: 'help', color: 'var(--color-solid-info)' },
};

export const ENDED_REASON_LABELS: Record<string, string> = {
  completed_term: 'Encerrado ao fim do período contratado',
  plan_transition: 'Encerrado por troca de plano',
  manual_cancel: 'Encerrado manualmente',
  checkout_failed: 'Encerrado porque o checkout não foi concluído',
};

export const TRANSITION_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Troca agendada',
  processing: 'Troca em processamento',
  completed: 'Troca concluída',
  failed: 'Troca com falha',
  canceled: 'Troca cancelada',
};

export const PAUSE_GATEWAY_DESCRIPTIONS: Record<GatewayProvider, string> = {
  asaas: 'No Asaas, as cobranças pendentes no período da pausa são canceladas automaticamente e ficam no histórico do portal como Pausado. Cobranças já pagas permanecem. Se uma cobrança nova surgir durante a pausa, ela também é cancelada e marcada como Pausado.',
  stripe: 'No Stripe, a coleta da assinatura fica suspensa (pause_collection) até o dia seguinte à data escolhida. Faturas que surgirem no período da pausa são canceladas e ficam no histórico do portal como Pausado.',
  paypal: 'No PayPal, a pausa não é aplicada automaticamente. Registraremos a pausa no portal, mas será necessário suspender a assinatura manualmente no painel do PayPal e retomá-la ao final da pausa.',
};

export const RESUME_GATEWAY_DESCRIPTIONS: Record<GatewayProvider, string> = {
  asaas: 'No Asaas, as cobranças que foram excluídas durante a pausa não serão recriadas. A próxima cobrança do ciclo normal cobre o retorno.',
  stripe: 'No Stripe, a suspensão da coleta (pause_collection) é removida imediatamente e as cobranças recorrentes voltam a ser emitidas no próximo ciclo.',
  paypal: 'No PayPal, registraremos o retorno no portal, mas você precisará reativar a assinatura manualmente no painel do PayPal se ela estiver suspensa.',
};

export const today = new Date();
today.setHours(0, 0, 0, 0);

export const extractPayloadField = (payload: Record<string, unknown>, field: string): string =>
  (payload?.[field] as string) ?? '';

const STATUS_MAP: Record<string, string> = {
  // Canonical statuses
  paid: 'PAID',
  pending: 'PENDING',
  canceled: 'CANCELED',
  cancelled: 'CANCELED',
  removed: 'CANCELED',
  expired: 'EXPIRED',
  failed: 'FAILED',
  refunded: 'REFUNDED',
  paused: 'PAUSED',
  pausado: 'PAUSED',
  pausada: 'PAUSED',
  credit: 'CREDIT',
  credito: 'CREDIT',
  crédito: 'CREDIT',
  unknown: 'UNKNOWN',

  // Legacy aliases required by migration mapping
  succeeded: 'PAID',
  confirmed: 'PAID',
  received: 'PAID',
  overdue: 'EXPIRED',
  deleted: 'CANCELED',
};

export function normalizeStatus(raw: string): string {
  const lower = raw.trim().toLowerCase();
  if (!lower) return 'UNKNOWN';
  return STATUS_MAP[lower] ?? 'UNKNOWN';
}
